/**
 * Guards background refetches of live state (Verläufe list, open thread).
 *
 * Every local change — an applied SSE event, an optimistic update, a POST
 * response — ticks the clock. A silent GET (reconnect `ready`, degraded
 * polling, reload for an unknown thread) that was started before the latest
 * tick carries a snapshot that is older than what is on screen: applying it
 * would roll the newest message or takeover back. Visible loads (the user
 * picked a conversation or filter) keep their own sequence logic.
 */
export interface MutationClock {
  /** Current tick; compare two readings to see whether anything changed in between. */
  readonly value: number;
  /** Record a local change. */
  tick: () => void;
}

export function createMutationClock(): MutationClock {
  let value = 0;
  return {
    get value() {
      return value;
    },
    tick: () => {
      value += 1;
    },
  };
}

/** A silent refresh is retried this often before it gives up for this round. */
export const SILENT_FETCH_ATTEMPTS = 3;

/**
 * Run a background fetch whose result is only valid when no local change
 * happened while it was in flight. An overtaken snapshot is discarded and the
 * fetch repeated — the refresh still has to cover what the stream missed
 * (right after `ready` the replayed events usually overtake the first GET).
 * Resolves `null` when every attempt was overtaken; errors propagate.
 */
export async function fetchUnlessOvertaken<T>(
  clock: MutationClock,
  fetcher: () => Promise<T>,
  attempts: number = SILENT_FETCH_ATTEMPTS,
): Promise<T | null> {
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const startedAt = clock.value;
    const result = await fetcher();
    if (clock.value === startedAt) return result;
  }
  return null;
}
